import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  Signal,
  ViewChild,
  viewChildren,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ProductSpec, SpecOption, Variation} from '@sportngin/se-commerce-sdk/models/clare';
import {Subject, takeUntil} from 'rxjs';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {
  SeFeFormFieldMultiselectComponent,
  SeFeFormFieldMultiselectModule,
  SeFeMultiselectItem,
  SeFeMultiselectOptions,
} from 'se-fe-form-field-multiselect';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFePositionService} from 'se-fe-position';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeTableDataSource, SeFeTableModule} from 'se-fe-table';
import {SeFeTooltipModule} from 'se-fe-tooltip';
import {MAX_ALLOWED_VARIANTS} from '../../../../../common/constants';
import {SpecsAndVariants, UpdateSpecOption, UpdateSpecs} from '../../../../../models';
import {generateRandomString} from '../../../../../utils/string-utils';
import {UpdateProductDetail} from '../product-detail.store';

interface SelectConfig {
  selectControl: SeFeMultiselectOptions;
  label: {label: string};
  id: number | undefined;
}

function variantIncludesOption(variant: Variation, option: UpdateSpecOption): boolean {
  return (variant.spec_options?.findIndex(o => o.id === option.option.id) ?? -1) > -1;
}

function extractVariantSpecValues(variant: Variation): string {
  return variant.spec_options?.map(o => o.product_spec?.name).join('/') ?? '';
}

function createSpec(name: string): ProductSpec {
  // JMB [2025-11-17]: The generated ID is still required for tracking within the form even though the spec is new
  // the ID will be ignored when sending to the API
  const id = Date.now() + Math.floor(Math.random() * 10000);

  return {id: id, name: name};
}

function specOptionIdToControlValue(option: SpecOption): string {
  return String(option.id);
}

function variantToSkuRow(variant: Variation): {name: string | undefined; sku: string; id: string} {
  const name = variant.name;
  const sku = variant.sku || '';
  const id = String(variant.id);
  return {name, sku, id};
}

@Component({
  selector: 'product-specs-modal',
  templateUrl: 'product-specs-modal.component.html',
  styleUrls: ['product-specs-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    SeFeFormFieldModule,
    SeFeFormFieldMultiselectModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeModalModule,
    SeFeSpinnerModule,
    SeFeAlertModule,
    SeFeTableModule,
    SeFeTooltipModule,
    SeFeFormFieldTextModule,
    SeFeButtonModule,
  ],
  providers: [SeFePositionService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {class: 'product-specs-modal'},
})
export class ProductSpecsModalComponent implements OnDestroy {
  @Output() save = new EventEmitter<UpdateProductDetail<SpecsAndVariants>>();
  @ViewChild('variantModal') variantModal: SeFeModalComponent;

  selectControls = viewChildren(SeFeFormFieldMultiselectComponent);

  originalSkuValues: Record<string, string> = {};
  isSaving = false;

  specs = input.required<ProductSpec[]>();
  variants = input.required<Variation[]>();
  error = input<string>();
  showTrigger = input(true);

  optionsToRemove = signal<UpdateSpecOption[]>([]);
  optionsToAdd = signal<UpdateSpecOption[]>([]);
  formValues = signal<Record<string, string[]>>({});

  // JMB [2024-06-04]: computed values adjust for cases where there are no existing specs for the product
  specValues: Signal<ProductSpec[]> = computed(() => {
    let result: ProductSpec[];
    if (this.specs().length === 0) {
      const colors: ProductSpec = createSpec('Colors');
      const sizes: ProductSpec = createSpec('Sizes');
      result = [colors, sizes];
    } else if (this.specs().length === 1) {
      const specName = this.specs()[0].name === 'Colors' ? 'Sizes' : 'Colors';
      const newSpec: ProductSpec = createSpec(specName);
      result = [...this.specs(), newSpec];
    } else {
      result = [...this.specs()];
    }
    return result;
  });
  specOptions: Signal<SelectConfig[]> = computed(() => {
    const validSpecs = this.specValues().filter((spec): spec is ProductSpec => {
      return !!spec && typeof spec.name === 'string' && spec.name.length > 0;
    });
    return validSpecs.map(spec => {
      return {
        selectControl: {
          sections: [
            {
              options: this.buildOptions(spec),
            },
          ],
        },
        label: {label: spec.name!, required: false},
        id: spec.id,
      };
    });
  });
  specMap: Signal<Record<string, SpecOption[]>> = computed(() => {
    return this.specValues().reduce((acc: Record<string, SpecOption[]>, spec) => {
      acc[spec.name!] = spec.spec_options ? [...spec.spec_options] : [];
      return acc;
    }, {});
  });
  specForm: Signal<FormGroup> = computed(() => {
    const form = this.fb.group({});
    this.specValues().forEach(spec => {
      const options = (spec.spec_options ?? []).map(specOptionIdToControlValue);
      form.addControl(spec.name, this.fb.control(options));
    });
    return form;
  });
  readonlySpecs: Signal<Record<string, boolean>> = computed(() => {
    const currentFormValues = this.formValues();

    return this.specValues().reduce((acc: Record<string, boolean>, spec) => {
      const currentOptionsCount = currentFormValues[spec.name]?.length || 0;
      const potentialTotal = this.specValues().reduce((total, s) => {
        const optionsCount = s.id === spec.id ? currentOptionsCount + 1 : currentFormValues[s.name]?.length || 1;
        return total * optionsCount;
      }, 1);

      acc[spec.name!] = potentialTotal > MAX_ALLOWED_VARIANTS;
      return acc;
    }, {});
  });
  optionRemovalWarnings: Signal<string[]> = computed(() => {
    let result: string[] = [];
    this.optionsToRemove().forEach(option => {
      const affectedVariants = this.variants()
        .filter(v => variantIncludesOption(v, option))
        .map(extractVariantSpecValues);

      result = [...result, ...affectedVariants];
    });
    return [...new Set(result)];
  });
  optionRemovalLabel: Signal<string> = computed(() => {
    const optionRemovalCount = this.optionsToRemove().length;
    const affectedVariantCount = this.optionRemovalWarnings().length;

    return `Removing ${optionRemovalCount} options will remove ${affectedVariantCount} Product variants.`;
  });

  variantTableRows: Signal<{name: string | undefined; sku: string; id: string}[]> = computed(
    () => this.variants().map(variantToSkuRow) || []
  );

  skuDatasource: Signal<SeFeTableDataSource> = computed(() => {
    return new SeFeTableDataSource(this.variantTableRows());
  });

  variantsForm: Signal<FormGroup> = computed(() => {
    const form = new FormGroup({});
    for (const v of this.variantTableRows()) {
      if (this.originalSkuValues[v.id] !== v.sku) {
        this.originalSkuValues[v.id] = v.sku;
      }
      form.addControl(v.id, new FormControl(v.sku));
    }
    return form;
  });

  variantModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      disabled: false,
      label: 'Cancel',
      action: () => {
        this.resetForm();
        this.variantModal.close();
        return 'Cancel';
      },
    },
    {
      primary: true,
      disabled: false,
      label: 'Save',
      action: async () => {
        this.saveProductSpecs();
        return 'Saving';
      },
      loadingLabel: 'Updating Product',
    },
  ];

  specsValidationConfig: SeFeFormFieldConfig = {
    label: '',
    errorMessages: {
      maxlength: 'Value exceeds maximum length',
    },
  };

  variantTableColumns = ['name', 'sku'];

  skuFormFieldConfig: SeFeFormFieldConfig = {
    label: 'Edit SKU',
    hiddenLabel: true,
  };

  specsMaxLengthValidator = Validators.maxLength(100);

  private destroySubject = new Subject();

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    effect(onCleanup => {
      const form = this.specForm();

      this.formValues.set(form.getRawValue());
      this.determineSpecificationChanges();

      const subscription = form.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => {
        this.formValues.set(form.getRawValue());
        this.determineSpecificationChanges();
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  ngOnDestroy() {
    this.destroySubject.next(null);
    this.destroySubject.complete();
  }

  displayTooltip(id: string): boolean {
    return !!this.readonlySpecs()[id];
  }

  open(focus: Event | Element): void {
    this.variantModal.open(focus);
  }

  closeModal(): void {
    this.cancelSaveState();
    this.resetForm();
    this.variantModal.close();
  }

  /**
   * Builds multiselect options from spec options, falling back to defaults when the API omits them.
   */
  buildOptions(spec: ProductSpec | undefined): SeFeMultiselectItem[] {
    const specName = spec?.name ?? '';
    const rawSpecOptions = spec?.spec_options;
    const specOptions = Array.isArray(rawSpecOptions) ? rawSpecOptions : [];
    const hasOptions = specOptions.some(() => true);
    const text = specName === 'Colors' ? 'Black' : 'Small';
    const value = generateRandomString(22);
    const result = hasOptions
      ? specOptions.map(option => {
          return {text: option.name!, value: String(option.id)};
        })
      : [{text, value}];
    return result;
  }

  getSelectPlaceholderText(specName: string): string {
    const knownNamesToStrip = ['Colors', 'Sizes'];
    const controlValue: string[] = this.specForm().value[specName] || [];
    // JMB [2024-06-03]: this will likely have to change once we start allowing users to create new Specs
    let noun = knownNamesToStrip.includes(specName) ? specName.slice(0, -1) : specName;
    noun = noun.toLocaleLowerCase();
    return controlValue.length > 0 ? `Add another ${noun}` : `Add a ${noun}`;
  }

  resetForm(): void {
    this.specValues().forEach(spec => {
      const options = this.specMap()[spec.name].map(specOptionIdToControlValue);
      this.specForm().controls[spec.name].setValue(options);
    });
    this.resetVariantsForm();

    this.optionsToRemove.set([]);
    this.optionsToAdd.set([]);
    /**
     * Avoid running an immediate nested change detection pass during modal close/open transitions.
     * Forcing `detectChanges()` here can trigger NG0100 in projected SeFe modal buttons.
     */
    queueMicrotask(() => this.cdr.markForCheck());
  }

  resetVariantsForm(): void {
    const currentVariantsForm = this.variantsForm();
    Object.keys(currentVariantsForm.controls).forEach(variantId => {
      const originalSku = this.originalSkuValues[variantId] || null;
      currentVariantsForm.controls[variantId].setValue(originalSku);
    });
    currentVariantsForm.markAsPristine();
    currentVariantsForm.markAsUntouched();
  }

  determineSpecificationChanges(): void {
    let optionsToRemove: UpdateSpecOption[] = [];
    let optionsToAdd: UpdateSpecOption[] = [];

    this.specValues().forEach(spec => {
      const existingOptions: string[] = this.specMap()[spec.name].map(o => String(o.id));
      const selectedOptions: string[] = this.specForm().value[spec.name] || [];
      const toRemove = this.difference(existingOptions, selectedOptions);
      const toAdd = this.difference(selectedOptions, existingOptions);

      if (toAdd.length > 0) {
        optionsToAdd = [...optionsToAdd, ...this.buildAddSpecOptions(toAdd, spec)];
      }

      optionsToRemove = [...optionsToRemove, ...this.buildRemoveSpecOptions(toRemove, spec)];
    });
    this.optionsToRemove.set(optionsToRemove);
    this.optionsToAdd.set(optionsToAdd);
  }

  buildRemoveSpecOptions(optionIds: string[], spec: ProductSpec): UpdateSpecOption[] {
    let result: UpdateSpecOption[] = [];
    optionIds.forEach(optionId => {
      const option = this.specMap()[spec.name].find(o => String(o.id) === optionId)!;
      result = [...result, {specId: spec.id!, option}];
    });
    return result;
  }

  buildAddSpecOptions(optionIds: string[], spec: ProductSpec): UpdateSpecOption[] {
    const controlId = spec.name;
    const control: SeFeFormFieldMultiselectComponent | undefined = this.selectControls().find(
      select => select.elementRef.nativeElement.id === controlId
    );

    return control!.selectedOptions
      .filter(item => optionIds.includes(item.value))
      .map(item => {
        const option: SpecOption = {id: Number(item.value), name: item.text};
        const result: UpdateSpecOption = {specId: spec.id!, option};
        return result;
      });
  }

  getNewSpecs(): ProductSpec[] {
    const existing = this.specs().map(s => s.id);
    const specIds = this.optionsToAdd().map(o => o.specId);
    return this.specValues().filter(s => {
      return !existing.some(e => e === s.id) && specIds.some(id => s.id === id);
    });
  }

  saveProductSpecs(): void {
    const variants = this.variantsForm().dirty ? this.mapVariantsFormToVariants() : [];

    const updateSpecs: UpdateSpecs = {
      specs: this.getNewSpecs(),
      optionsToAdd: this.optionsToAdd(),
      optionsToRemove: this.optionsToRemove(),
    };
    this.isSaving = true;
    this.cdr.markForCheck();
    this.save.emit({
      data: {updateSpecs, variants},
      modal: this.getModalController(),
    });
  }

  mapVariantsFormToVariants(): Variation[] {
    const result: Variation[] = [];
    for (const [key, value] of Object.entries(this.variantsForm().value)) {
      if (this.variantsForm().controls[key].dirty) {
        const variant: Variation = {id: Number(key), sku: String(value)};
        result.push(variant);
      }
    }
    return result;
  }

  private difference(a: string[], b: string[]): string[] {
    return a.filter(x => !b.includes(x));
  }

  private getModalController(): SeFeModalComponent {
    return {
      close: () => {
        this.cancelSaveState();
        this.variantModal.close();
      },
      cancelSpinner: () => {
        this.cancelSaveState();
      },
    } as SeFeModalComponent;
  }

  private cancelSaveState(): void {
    this.isSaving = false;
    this.cdr.markForCheck();
  }

  protected readonly MAX_ALLOWED_VARIANTS = MAX_ALLOWED_VARIANTS;
}
